import { TextField } from "@mui/material";
import { useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../api/adminApi";
import type { Approval, ApproverAssignment } from "../../api/types/approvalTypes";
import useUserContext from "../../context/useUserContext";
import ApproversAssignmentDialog from "./workflows/ApproversAssignmentDialog";
import { getInitialApproversAssignmentStateFromPendingApproval } from "./workflows/approversAssignmentState";

const reassignApproval = withErrorHandling(adminApi.reassignApproval);

interface Props {
  approval: Approval;
  onClose: () => void;
  onApply: (updatedApproval: Approval) => void;
}

const ReassignApproversDialog = ({ approval, onClose, onApply }: Props) => {
  const { id: userId } = useUserContext();

  const { sendNotification, sendNotificationError } = useNotificationContext();

  const [saving, setSaving] = useState(false);
  const [comment, setComment] = useState("");

  const handleConfirm = async (approverAssignments: ApproverAssignment[]) => {
    setSaving(true);
    const [updatedApproval, error] = await reassignApproval(approval.id, {
      commentUserInput: comment.trim() || undefined,
      approverAssignments,
    });
    setSaving(false);

    if (error) {
      logError(error, "[ReassignApproversDialog] reassignApproval");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Approvers reassigned");
    onApply(updatedApproval);
    onClose();
  };

  return (
    <ApproversAssignmentDialog
      initialState={getInitialApproversAssignmentStateFromPendingApproval(approval, userId)}
      actionName="Reassign"
      loading={saving}
      onConfirm={handleConfirm}
      onClose={onClose}
    >
      <TextField
        fullWidth
        multiline
        rows={3}
        label="Comment (optional)"
        value={comment}
        onChange={(e) => setComment(e.target.value)}
        sx={{ mt: 2 }}
      />
    </ApproversAssignmentDialog>
  );
};

export default ReassignApproversDialog;
